import type { KeySlot } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import { useState, type FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import type { CofSubMode } from './cof-wheel-params.js'
import styles from './cof-wheel-welcome.module.css'

export interface CofWheelWelcomeProps {
  preSelectedSubMode: CofSubMode | null
  preSelectedKey: KeySlot | null
  onStartExplore: (key: { name: string; ring: 'major' | 'minor' }) => void
  onStartQuiz: () => void
}

/**
 * CoF Wheel v1 welcome surface — two sub-mode cards. Click Explore to enter
 * the free-explore wheel (with C major as default unless a `?key=` param
 * pre-selected another); click Quiz to start a 10-question session.
 *
 * Spec: docs/superpowers/specs/2026-05-23-cof-wheel-v1-design.md §9
 */
export const CofWheelWelcome: FC<CofWheelWelcomeProps> = props => {
  const { preSelectedSubMode, preSelectedKey, onStartExplore, onStartQuiz } = props
  const [activeSubMode] = useState<CofSubMode | null>(preSelectedSubMode)

  const toolCopy = useTheoryCopy('cof-wheel')
  const exploreCopy = useTheoryCopy('cof-explore')
  const quizCopy = useTheoryCopy('cof-quiz')

  const handleExplore = (): void => {
    const key = preSelectedKey ? preSelectedKey.canonical : 'C'
    onStartExplore({ name: key, ring: 'major' })
  }

  return (
    <div className={styles.welcome}>
      <header className={styles.intro}>
        <h1 className={styles.title}>{toolCopy?.displayName ?? 'Circle of Fifths'}</h1>
        {toolCopy?.body ? <p className={styles.tagline}>{toolCopy.body}</p> : null}
      </header>

      <div className={styles.subModeGrid}>
        <Button
          type="button"
          variant="outlined"
          onClick={handleExplore}
          aria-pressed={activeSubMode === 'explore'}
          className={styles.subModeCard}
        >
          <span className={styles.cardTitle}>{exploreCopy?.displayName ?? 'Wheel Explore'}</span>
          {exploreCopy?.body ? <span className={styles.cardBody}>{exploreCopy.body}</span> : null}
        </Button>
        <Button
          type="button"
          variant="outlined"
          onClick={onStartQuiz}
          aria-pressed={activeSubMode === 'quiz'}
          className={styles.subModeCard}
        >
          <span className={styles.cardTitle}>{quizCopy?.displayName ?? 'Key Relationship Quiz'}</span>
          {quizCopy?.body ? <span className={styles.cardBody}>{quizCopy.body}</span> : null}
        </Button>
      </div>
    </div>
  )
}
